<script lang="ts" setup>
import { generateMetaDescription } from '~/utils'

const route = useRoute()
const { translation, bookCode } = route.params as { translation: string; bookCode: string }

const { data: book, error } = await useBibleBook(translation, bookCode)
const { data: chapters, status: chaptersStatus } = useBibleChapters(translation, bookCode)
const { data: books } = useBibleBooks(translation)

if (error.value && error.value.statusCode === 404) {
  throw showError({
    statusCode: 404,
    statusMessage: 'Page Not Found',
  })
}

watchEffect(() => {
  if (book.value) {
    useSeo({
      title: book.value.fullTitle,
      description: generateMetaDescription(book.value.summary),
    })
  }
})

const nextBook = computed(() => {
  if (!book.value || !books.value) return null
  const bookNumber = book.value.number + 1
  return books.value.find((b) => b.number === bookNumber)
})

const prevBook = computed(() => {
  if (!book.value || !books.value) return null
  const bookNumber = book.value.number - 1
  return books.value.find((b) => b.number === bookNumber)
})
</script>

<template>
  <div v-if="book" class="sm:space-y-8 space-y-6">
    <div class="flex sm:flex-row flex-col gap-8">
      <div class="flex sm:justify-start justify-center">
        <div
          class="w-64 rounded-xl relative aspect-[1.75/1] border border-zeno-gray-border-light hover:border-zeno-gray-border bg-zeno-gray-light"
        >
          <NuxtImg
            :alt="book.fullTitle"
            :src="`/img/bible/${book.testament}/${book.code}.webp`"
            class="w-full object-center object-cover rounded-xl aspect-[1.75/1] bg-zeno-gray-light"
          />
        </div>
      </div>

      <div class="flex flex-row items-center sm:justify-start justify-center">
        <div class="flex flex-col gap-5">
          <PageTitle class="sm:text-left text-center">
            {{ book.fullTitle }}
          </PageTitle>

          <div class="flex space-x-3 sm:justify-start justify-center">
            <TagLink :href="`/bible/${book.translation}/testament/${book.testament}/`">
              <span class="capitalize">{{ book.testament }}</span> Testament
            </TagLink>

            <BibleBookTranslationTagLink :book="book" />
          </div>
        </div>
      </div>
    </div>

    <div v-if="book.summary" class="md:pl-72">
      <DescriptionText :text="book.summary" />
    </div>

    <div class="md:space-y-12 space-y-6">
      <div class="md:space-y-8 space-y-6">
        <div class="border-b border-zeno-gray-border flex space-x-4 md:justify-start justify-between">
          <div class="px-6 pb-4 text-lg border-b border-zeno-text flex items-center">
            Chapters
            <span class="bg-zeno-gray-light rounded-full text-zeno-gray text-xs p-1 ml-2 font-semibold">
              {{ book.chapterCount }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <BibleChapterGrid :book="book" :chapters="chapters" :status="chaptersStatus" />

    <div class="flex items-center justify-between text-3xl font-monigue">
      <NuxtLink v-if="prevBook" :href="`/bible/${prevBook.translation}/${prevBook.code}/`">
        &lt; {{ prevBook.title }}
      </NuxtLink>

      <NuxtLink v-if="nextBook" :href="`/bible/${nextBook.translation}/${nextBook.code}/`">
        {{ nextBook.title }} &gt;
      </NuxtLink>
    </div>
  </div>
</template>

<style scoped></style>
